import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Trash2, Plus, X } from "lucide-react";

const emptyForm = (date) => ({
  client_name: "",
  service_type: "",
  contact: "",
  address_from: "",
  address_to: "",
  deposit: 0,
  hourly_rate: 0,
  estimated_hours: 0,
  actual_hourly_rate: 0,
  actual_hours: 0,
  extra_charges: [],
  total_payment: 0,
  expenses: [],
  date: date || new Date().toISOString().slice(0, 10),
  start_time: "09:00",
  end_time: "12:00",
  notes: "",
  status: "scheduled",
});

export default function JobDialog({ open, onOpenChange, job, defaultDate, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState(emptyForm(defaultDate));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Whether the user manually overrode total_payment. If not, we always show/save the auto-computed total.
  const [totalTouched, setTotalTouched] = useState(false);

  useEffect(() => {
    if (job) {
      setForm({ ...emptyForm(), ...job });
      // Detect if the persisted total_payment is an override (differs from labor + extras).
      const laborA = (parseFloat(job.actual_hourly_rate) || 0) * (parseFloat(job.actual_hours) || 0);
      const laborE = (parseFloat(job.hourly_rate) || 0) * (parseFloat(job.estimated_hours) || 0);
      const eff = laborA > 0 ? laborA : laborE;
      const extras = (job.extra_charges || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
      const auto = eff + extras;
      const manual = parseFloat(job.total_payment) || 0;
      setTotalTouched(manual > 0 && Math.abs(manual - auto) > 0.01);
    } else {
      setForm(emptyForm(defaultDate));
      setTotalTouched(false);
    }
  }, [job, defaultDate, open]);

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addExpense = () => setForm((p) => ({ ...p, expenses: [...p.expenses, { label: "", amount: 0 }] }));
  const updateExpense = (i, k, v) =>
    setForm((p) => ({
      ...p,
      expenses: p.expenses.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)),
    }));
  const removeExpense = (i) => setForm((p) => ({ ...p, expenses: p.expenses.filter((_, idx) => idx !== i) }));

  const addExtra = () => setForm((p) => ({ ...p, extra_charges: [...(p.extra_charges || []), { label: "", amount: 0 }] }));
  const updateExtra = (i, k, v) =>
    setForm((p) => ({
      ...p,
      extra_charges: (p.extra_charges || []).map((e, idx) => (idx === i ? { ...e, [k]: v } : e)),
    }));
  const removeExtra = (i) => setForm((p) => ({ ...p, extra_charges: (p.extra_charges || []).filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const _actual = (parseFloat(form.actual_hourly_rate) || 0) * (parseFloat(form.actual_hours) || 0);
      const _est = (parseFloat(form.hourly_rate) || 0) * (parseFloat(form.estimated_hours) || 0);
      const _extras = (form.extra_charges || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
      const _computed = ((_actual > 0 ? _actual : _est) + _extras);
      const _manual = parseFloat(form.total_payment) || 0;
      // Persist an effective total. If the user hasn't manually overridden, always store the computed one
      // so tables, PDFs and reports stay in sync with the latest rate/hours/extras.
      const effectiveTotal = totalTouched && _manual > 0 ? _manual : _computed;
      const payload = {
        ...form,
        deposit: parseFloat(form.deposit) || 0,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        estimated_hours: parseFloat(form.estimated_hours) || 0,
        actual_hourly_rate: parseFloat(form.actual_hourly_rate) || 0,
        actual_hours: parseFloat(form.actual_hours) || 0,
        extra_charges: (form.extra_charges || []).map((x) => ({ label: x.label, amount: parseFloat(x.amount) || 0 })),
        total_payment: effectiveTotal,
        expenses: form.expenses.map((x) => ({ label: x.label, amount: parseFloat(x.amount) || 0 })),
      };
      if (job?.id) await api.put(`/jobs/${job.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(job?.id ? "Job updated" : "Job created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!job?.id) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted");
      setConfirmDelete(false);
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const totalExpenses = form.expenses.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const estimatedTotal = (parseFloat(form.hourly_rate) || 0) * (parseFloat(form.estimated_hours) || 0);
  const laborActual = (parseFloat(form.actual_hourly_rate) || 0) * (parseFloat(form.actual_hours) || 0);
  const extrasTotal = (form.extra_charges || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  // Fall back to estimated when actual hasn't been entered yet — same logic as backend effective_income.
  const effectiveLabor = laborActual > 0 ? laborActual : estimatedTotal;
  const computedTotal = effectiveLabor + extrasTotal;
  const manualTotal = parseFloat(form.total_payment) || 0;
  const useTotal = totalTouched && manualTotal > 0 ? manualTotal : computedTotal;
  const profit = useTotal - totalExpenses;
  const balance = useTotal - (parseFloat(form.deposit) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-card border-border"
        data-testid="job-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "Outfit" }}>
            {job?.id ? t.portal.job.editTitle : t.portal.job.title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t.portal.job.client} *</Label>
              <Input required value={form.client_name} onChange={(e) => updateField("client_name", e.target.value)} className="bg-background mt-1" data-testid="job-client-input" />
            </div>
            <div>
              <Label>{t.portal.job.contact} *</Label>
              <Input required value={form.contact} onChange={(e) => updateField("contact", e.target.value)} className="bg-background mt-1" data-testid="job-contact-input" />
            </div>
          </div>

          <div>
            <Label>{t.portal.job.serviceType} *</Label>
            <Select value={form.service_type} onValueChange={(v) => updateField("service_type", v)}>
              <SelectTrigger className="bg-background mt-1" data-testid="job-service-select">
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {t.portal.services.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t.portal.job.addressFrom}</Label>
              <Input value={form.address_from} onChange={(e) => updateField("address_from", e.target.value)} className="bg-background mt-1" data-testid="job-from-input" />
            </div>
            <div>
              <Label>{t.portal.job.addressTo}</Label>
              <Input value={form.address_to} onChange={(e) => updateField("address_to", e.target.value)} className="bg-background mt-1" data-testid="job-to-input" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t.portal.job.date} *</Label>
              <Input type="date" required value={form.date} onChange={(e) => updateField("date", e.target.value)} className="bg-background mt-1" data-testid="job-date-input" />
            </div>
            <div>
              <Label>{t.portal.job.startTime}</Label>
              <Input type="time" value={form.start_time} onChange={(e) => updateField("start_time", e.target.value)} className="bg-background mt-1" />
            </div>
            <div>
              <Label>{t.portal.job.endTime}</Label>
              <Input type="time" value={form.end_time} onChange={(e) => updateField("end_time", e.target.value)} className="bg-background mt-1" />
            </div>
          </div>

          <div>
            <Label>{t.portal.job.deposit} ($)</Label>
            <Input type="number" step="0.01" value={form.deposit} onChange={(e) => updateField("deposit", e.target.value)} className="bg-background mt-1 max-w-[200px]" data-testid="job-deposit-input" />
          </div>

          {/* CONTRACTED section */}
          <div className="rounded-lg border border-border p-4 bg-background/40">
            <Label className="text-base flex items-center justify-between">
              <span>Contracted with client</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Estimated at booking</span>
            </Label>
            <div className="grid sm:grid-cols-3 gap-4 mt-3">
              <div>
                <Label className="text-xs">Price per hour ($)</Label>
                <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => updateField("hourly_rate", e.target.value)} className="bg-card mt-1" data-testid="job-hourly-rate-input" />
              </div>
              <div>
                <Label className="text-xs">Estimated hours</Label>
                <Input type="number" step="0.25" value={form.estimated_hours} onChange={(e) => updateField("estimated_hours", e.target.value)} className="bg-card mt-1" data-testid="job-estimated-hours-input" />
              </div>
              <div>
                <Label className="text-xs text-primary">Estimated total</Label>
                <div className="mt-1 h-10 px-3 rounded-md border border-primary/30 bg-primary/10 flex items-center font-bold text-primary" data-testid="job-estimated-total">
                  ${estimatedTotal.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* ACTUAL section */}
          <div className="rounded-lg border border-border p-4 bg-background/40">
            <Label className="text-base flex items-center justify-between">
              <span>Actual worked</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Real hours & rate</span>
            </Label>
            <div className="grid sm:grid-cols-3 gap-4 mt-3">
              <div>
                <Label className="text-xs">Actual price/hour ($)</Label>
                <Input type="number" step="0.01" value={form.actual_hourly_rate} onChange={(e) => updateField("actual_hourly_rate", e.target.value)} className="bg-card mt-1" data-testid="job-actual-rate-input" />
              </div>
              <div>
                <Label className="text-xs">Actual hours</Label>
                <Input type="number" step="0.25" value={form.actual_hours} onChange={(e) => updateField("actual_hours", e.target.value)} className="bg-card mt-1" data-testid="job-actual-hours-input" />
              </div>
              <div>
                <Label className="text-xs">Labor subtotal</Label>
                <div className="mt-1 h-10 px-3 rounded-md border border-border bg-card flex items-center font-semibold" data-testid="job-labor-subtotal">
                  ${laborActual.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* EXTRA CHARGES */}
          <div className="rounded-lg border border-border p-4 bg-background/40">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-base">Extra charges (client pays)</Label>
                <div className="text-xs text-muted-foreground mt-0.5">Material, elevator fee, stairs, packing, etc.</div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addExtra} className="rounded-full" data-testid="job-add-extra-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add charge
              </Button>
            </div>
            {(form.extra_charges || []).length === 0 && <div className="text-xs text-muted-foreground">No extra charges</div>}
            <div className="space-y-2">
              {(form.extra_charges || []).map((exp, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`job-extra-row-${i}`}>
                  <Input placeholder="Concept (e.g. Elevator fee)" value={exp.label} onChange={(e) => updateExtra(i, "label", e.target.value)} className="bg-card flex-1" />
                  <Input type="number" step="0.01" placeholder="0.00" value={exp.amount} onChange={(e) => updateExtra(i, "amount", e.target.value)} className="bg-card w-32" />
                  <button type="button" onClick={() => removeExtra(i)} className="p-2 text-destructive hover:bg-destructive/10 rounded-md" aria-label="Remove">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            {(form.extra_charges || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border text-sm flex justify-between">
                <span className="text-muted-foreground">Subtotal extras</span>
                <span className="font-bold text-primary" data-testid="job-extras-subtotal">${extrasTotal.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* TOTAL row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t.portal.job.total} ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={totalTouched ? form.total_payment : (computedTotal ? computedTotal.toFixed(2) : "")}
                onChange={(e) => { setTotalTouched(true); updateField("total_payment", e.target.value); }}
                className="bg-background mt-1"
                placeholder={computedTotal.toFixed(2)}
                data-testid="job-total-input"
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>
                  {totalTouched
                    ? <>Auto: <span className="font-semibold text-foreground">${computedTotal.toFixed(2)}</span></>
                    : <span className="text-primary">Auto-calculated from labor + extras</span>}
                </span>
                {totalTouched && (
                  <button type="button" onClick={() => { setTotalTouched(false); updateField("total_payment", ""); }} className="text-primary hover:underline">
                    Use auto
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-primary font-bold">Balance due</span>
                <span className="text-lg font-bold text-primary" data-testid="job-balance">${balance.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border p-4 bg-background/40">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base">{t.portal.job.expenses}</Label>
              <Button type="button" size="sm" variant="outline" onClick={addExpense} className="rounded-full" data-testid="job-add-expense-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> {t.portal.job.addExpense}
              </Button>
            </div>
            {form.expenses.length === 0 && <div className="text-xs text-muted-foreground">No expenses added</div>}
            <div className="space-y-2">
              {form.expenses.map((exp, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder={t.portal.job.expenseLabel} value={exp.label} onChange={(e) => updateExpense(i, "label", e.target.value)} className="bg-card flex-1" />
                  <Input type="number" step="0.01" placeholder="0.00" value={exp.amount} onChange={(e) => updateExpense(i, "amount", e.target.value)} className="bg-card w-32" />
                  <button type="button" onClick={() => removeExpense(i)} className="p-2 text-destructive hover:bg-destructive/10 rounded-md">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            {form.expenses.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border text-sm flex justify-between">
                <span className="text-muted-foreground">Subtotal expenses</span>
                <span className="font-bold text-destructive">${totalExpenses.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t.portal.job.status}</Label>
              <Select value={form.status} onValueChange={(v) => updateField("status", v)}>
                <SelectTrigger className="bg-background mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Net profit</span>
                <span className="text-lg font-bold text-emerald-400" data-testid="job-net-profit">${profit.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <Label>{t.portal.job.notes}</Label>
            <Textarea value={form.notes} onChange={(e) => updateField("notes", e.target.value)} rows={3} className="bg-background mt-1" />
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            {job?.id && (
              <Button type="button" variant="destructive" onClick={() => setConfirmDelete(true)} className="rounded-full" data-testid="job-delete-btn">
                <Trash2 className="h-4 w-4 mr-1" /> {t.portal.job.delete}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
              {t.portal.job.cancel}
            </Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="job-save-btn">
              {saving ? "..." : t.portal.job.save}
            </Button>
          </DialogFooter>
        </form>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent className="bg-card border-border" data-testid="job-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this job?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The job will be permanently removed from your calendar and reports.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full" data-testid="job-delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="job-delete-confirm-btn">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
